import React, { useLayoutEffect, useRef, useState } from "react";

import {
  Paper,
  Stack,
  Table,
  TableContainer,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

export interface PaginatedTableShellProps {
  columns: React.ReactNode;
  headerAriaLabel: string;
  bodyAriaLabel: string;
  header: React.ReactNode;
  body: React.ReactNode;
  shownCount?: number;
  totalCount?: number;
  headerTableSx?: SxProps<Theme>;
  bodyTableSx?: SxProps<Theme>;
  headerContainerSx?: SxProps<Theme>;
  bodyContainerSx?: SxProps<Theme>;
  footerSx?: SxProps<Theme>;
}

/**
 * Reusable split-table shell with a fixed header, scrollable body, and optional footer.
 * The header reserves the measured scrollbar width from the body so columns stay aligned.
 */
const PaginatedTableShell: React.FC<PaginatedTableShellProps> = ({
  columns,
  headerAriaLabel,
  bodyAriaLabel,
  header,
  body,
  shownCount,
  totalCount,
  headerTableSx,
  bodyTableSx,
  headerContainerSx,
  bodyContainerSx,
  footerSx,
}) => {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  useLayoutEffect(() => {
    const bodyElement = bodyRef.current;
    if (!bodyElement) {
      return;
    }

    const measure = () => {
      setScrollbarWidth(bodyElement.offsetWidth - bodyElement.clientWidth);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(bodyElement);

    if (bodyElement.firstElementChild) {
      observer.observe(bodyElement.firstElementChild);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Paper
      elevation={5}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        borderRadius: "0.5rem",
      }}
    >
      <TableContainer
        sx={{
          flexShrink: 0,
          overflowY: "hidden",
          pr: `${scrollbarWidth}px`,
          borderBottom: 1,
          borderColor: "divider",
          background:
            "linear-gradient(0deg,rgb(217, 233, 247),rgb(217, 238, 247))",
          ...headerContainerSx,
        }}
      >
        <Table
          aria-label={headerAriaLabel}
          size="small"
          sx={{
            width: "100%",
            tableLayout: "fixed",
            "& .MuiTableHead-root": { background: "transparent" },
            "& .MuiTableCell-root": { borderBottom: 0 },
            ...headerTableSx,
          }}
        >
          {columns}
          {header}
        </Table>
      </TableContainer>

      <TableContainer
        ref={bodyRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          ...bodyContainerSx,
        }}
      >
        <Table
          aria-label={bodyAriaLabel}
          size="small"
          sx={{
            width: "100%",
            tableLayout: "fixed",
            ...bodyTableSx,
          }}
        >
          {columns}
          {body}
        </Table>
      </TableContainer>

      {shownCount !== undefined && totalCount !== undefined && (
        <Stack
          sx={{
            flexShrink: 0,
            borderTop: 1,
            borderColor: "divider",
            background: "linear-gradient(0deg,rgb(248, 251, 254),rgb(255, 255, 255))",
            ...footerSx,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: 40, px: 2 }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem" }}
            >
              Showing {shownCount} of {totalCount}
            </Typography>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

export default PaginatedTableShell;